import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Loader2, CheckCircle2 } from "lucide-react";
import { motion } from "framer-motion";

const RULE = "Le mot de passe doit contenir au moins 12 caractères, une majuscule, une minuscule, un chiffre et un caractère spécial.";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw !== pw2) { setError("Les mots de passe ne correspondent pas."); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      setTimeout(() => navigate("/login"), 2500);
    } catch (err) {
      setError(apiErr(err.response?.data?.detail) || "Erreur");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center sky-bg p-6">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
        <Card className="bg-white p-8 rounded-2xl border-0 shadow-2xl">
          <div className="mb-6 flex justify-center"><img src="/inzeair-logo.jpg" alt="InZeAir" className="w-36 rounded-xl" /></div>
          {!done ? (
            <>
              <h2 className="font-display font-bold text-2xl text-slate-900">Nouveau mot de passe</h2>
              <p className="text-sm text-slate-500 mb-6">{RULE}</p>
              {!token && <div className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2 mb-4">Lien invalide : jeton manquant.</div>}
              <form onSubmit={submit} className="space-y-4">
                <div className="grid gap-1.5">
                  <label className="text-sm font-medium text-slate-600">Nouveau mot de passe</label>
                  <Input data-testid="reset-password" type="password" value={pw} onChange={(e) => setPw(e.target.value)} required placeholder="••••••••" />
                </div>
                <div className="grid gap-1.5">
                  <label className="text-sm font-medium text-slate-600">Confirmer le mot de passe</label>
                  <Input data-testid="reset-password-confirm" type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} required placeholder="••••••••" />
                </div>
                {error && <div data-testid="reset-error" className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</div>}
                <Button data-testid="reset-submit" type="submit" disabled={busy || !token} className="w-full rounded-full h-11">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Réinitialiser"}
                </Button>
              </form>
            </>
          ) : (
            <div className="text-center py-4" data-testid="reset-success">
              <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto mb-4" />
              <p className="text-slate-700">Mot de passe réinitialisé. Redirection vers la connexion…</p>
            </div>
          )}
          <div className="text-center mt-6">
            <Link to="/login" className="text-sm text-primary hover:underline">Retour à la connexion</Link>
          </div>
        </Card>
      </motion.div>
    </div>
  );
}
